/**
 * An inspection's FEE per occurrence — what one 600 hr (say) costs beyond its BOM parts.
 * Aaron 2026-10-13 (Q1-b, via the Aircraft master): "new aircraft_inspections.estimated_cost
 * + currency, with an edit field". It feeds the Fleet Report's Cost Forecast.
 *
 * Posts `estimated_cost` / `estimated_cost_currency` ONLY once the user edits one: an
 * untouched form carries neither key, so the backend leaves the stored fee alone (an empty
 * amount posts null, which clears it). Empty is "no fee on file"; 0 is "costs nothing".
 *
 * version-34 reaches tenants one card at a time. Until it lands the backend serves
 * `inspection_fee_supported: false` and refuses a fee with 409, so the fields are shown
 * disabled with the reason instead of failing on Save. A NEW inspection has no flag yet:
 * its fee is added after the first save.
 */
import { TextField } from "@mui/material";

import type { InspectionDetailView } from "./types";

interface Props {
  values: InspectionDetailView;
  setFieldValue: (field: string, value: unknown) => void;
}

export default function InspectionFeeField({ values, setFieldValue }: Props) {
  const supported = values.inspection_fee_supported === true;
  const amount =
    values.estimated_cost !== undefined
      ? values.estimated_cost ?? ""
      : values.inspection_fee?.amount ?? "";
  const currency =
    values.estimated_cost_currency !== undefined
      ? values.estimated_cost_currency ?? ""
      : values.inspection_fee?.currency ?? "";
  const why = !values.id
    ? "Save the inspection first, then add its fee."
    : supported
      ? "Per occurrence, for the Cost Forecast. Empty = no fee on file."
      : // Customer-facing: never name the schema or the database (Shelley SOP item 23, 2026-10-15).
        "Not available on this site yet.";

  return (
    <>
      <TextField
        fullWidth
        label="Inspection Fee"
        name="estimated_cost"
        value={String(amount)}
        disabled={!supported}
        onChange={(e) => {
          const v = e.target.value.trim();
          setFieldValue("estimated_cost", v === "" ? null : v);
        }}
        inputProps={{ inputMode: "decimal" }}
        helperText={why}
      />
      <TextField
        fullWidth
        label="Fee Currency"
        name="estimated_cost_currency"
        value={String(currency)}
        disabled={!supported}
        onChange={(e) => {
          const v = e.target.value.trim().toUpperCase();
          setFieldValue("estimated_cost_currency", v === "" ? null : v);
        }}
        helperText="Empty = the system currency."
      />
    </>
  );
}
